const BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

// This browser or app install, so the server can email the account holder
// the first time their account is used somewhere new ("was this you?").
// A random number kept here, nothing about the person or the device; if
// it can't be stored (a private window), nothing is sent and no email
// goes. The kind of client is the app's own platform, or "web".
const CLIENT_ID_KEY = "grtp_client_id";
function signInClient() {
  let id = null;
  try {
    id = localStorage.getItem(CLIENT_ID_KEY);
    if (!id || !/^[a-f0-9]{32}$/.test(id)) {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      id = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      localStorage.setItem(CLIENT_ID_KEY, id);
    }
  } catch { return {}; }
  const platform = window.Capacitor && typeof window.Capacitor.getPlatform === "function" ? window.Capacitor.getPlatform() : "web";
  return { clientId: id, clientKind: ["ios", "android"].includes(platform) ? platform : "web" };
}
const TOKEN_KEY = "grtp_auth_token";
const SUPERADMIN_TOKEN_KEY = "grtp_superadmin_token";
const IS_ORG_ADMIN_KEY = "grtp_is_org_admin";
const USER_ID_KEY = "grtp_user_id";
const ORG_NAME_KEY = "grtp_org_name";
const ORG_TYPE_KEY = "grtp_org_type";
const ORG_BRAND_COLOR_KEY = "grtp_org_brand_color";
const ORG_LOGO_DATA_URL_KEY = "grtp_org_logo_data_url";

function getToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

function setToken(token) {
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
}

function getIsOrgAdmin() {
  return sessionStorage.getItem(IS_ORG_ADMIN_KEY) === "true";
}

function setIsOrgAdmin(value) {
  if (value) sessionStorage.setItem(IS_ORG_ADMIN_KEY, "true");
  else sessionStorage.removeItem(IS_ORG_ADMIN_KEY);
}

function getUserId() {
  return sessionStorage.getItem(USER_ID_KEY) || null;
}

function setUserId(id) {
  if (id) sessionStorage.setItem(USER_ID_KEY, id);
  else sessionStorage.removeItem(USER_ID_KEY);
}

function getOrgName() {
  return sessionStorage.getItem(ORG_NAME_KEY) || "";
}

function setOrgName(name) {
  if (name) sessionStorage.setItem(ORG_NAME_KEY, name);
  else sessionStorage.removeItem(ORG_NAME_KEY);
}

function getOrgType() {
  return sessionStorage.getItem(ORG_TYPE_KEY) || "";
}

function setOrgType(type) {
  if (type) sessionStorage.setItem(ORG_TYPE_KEY, type);
  else sessionStorage.removeItem(ORG_TYPE_KEY);
}

// Both optional — null/absent just means "use GRTP's own colours/logo",
// which is also the state every org starts in.
function getOrgBrandColor() {
  return sessionStorage.getItem(ORG_BRAND_COLOR_KEY) || null;
}

function setOrgBrandColor(color) {
  if (color) sessionStorage.setItem(ORG_BRAND_COLOR_KEY, color);
  else sessionStorage.removeItem(ORG_BRAND_COLOR_KEY);
}

function getOrgLogoDataUrl() {
  return sessionStorage.getItem(ORG_LOGO_DATA_URL_KEY) || null;
}

function setOrgLogoDataUrl(dataUrl) {
  if (dataUrl) sessionStorage.setItem(ORG_LOGO_DATA_URL_KEY, dataUrl);
  else sessionStorage.removeItem(ORG_LOGO_DATA_URL_KEY);
}

function getSuperadminToken() {
  return sessionStorage.getItem(SUPERADMIN_TOKEN_KEY);
}

function setSuperadminToken(token) {
  if (token) sessionStorage.setItem(SUPERADMIN_TOKEN_KEY, token);
  else sessionStorage.removeItem(SUPERADMIN_TOKEN_KEY);
}

// ── Device verification for public record links ──────────────────────
//
// Deliberately localStorage, not the sessionStorage everything else
// uses. A staff session ending when the tab closes is the point; this is
// the opposite — it exists so somebody answers a code about once a month
// rather than once a visit, which only works if it outlives the tab.
//
// It is not a credential on its own. It proves control of one mailbox at
// one organisation, and the server checks it against the pass being
// opened, so a device token for somewhere else opens nothing here.
//
// Several are kept because one phone can belong to a coach at two clubs,
// or to a household where two people are contacts. Wrapped because a
// browser with site data blocked throws on access rather than returning
// null, and the page must still render.
const PASS_DEVICE_KEY = "grtp_pass_devices";
const MAX_REMEMBERED_DEVICES = 10;

function passDeviceTokens() {
  try {
    const raw = localStorage.getItem(PASS_DEVICE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((t) => typeof t === "string" && t) : [];
  } catch {
    return [];
  }
}

function rememberPassDevice(token) {
  if (!token) return;
  try {
    const next = [token, ...passDeviceTokens().filter((t) => t !== token)].slice(0, MAX_REMEMBERED_DEVICES);
    localStorage.setItem(PASS_DEVICE_KEY, JSON.stringify(next));
  } catch {
    // A browser refusing to store it means verifying again next visit,
    // which is worse than it should be but not broken.
  }
}

function passHeaders() {
  const tokens = passDeviceTokens();
  return tokens.length ? { "x-pass-device": tokens.join(",") } : {};
}

async function req(method, path, body, opts = {}) {
  const headers = { "Content-Type": "application/json" };
  // opts.public marks a genuinely anonymous endpoint (parent portal,
  // org search, mailing list) — it must behave exactly like skipAuth:
  // never attach a staff session token (a shared browser could otherwise
  // leak a logged-in staff member's token onto a request meant to be
  // anonymous), and never treat that request's 401 as "your session
  // expired" (see below), since it was never using a session to begin with.
  if (!opts.skipAuth && !opts.public && !opts.superadminAuth) {
    const token = getToken();
    if (token) headers["x-auth-token"] = token;
  }
  if (opts.superadminAuth) {
    const superadminToken = getSuperadminToken();
    if (superadminToken) headers["x-superadmin-token"] = superadminToken;
  }
  // Device verification for public record links. Additive and never a
  // session: it says which mailbox this browser has proved control of,
  // and the server still checks that against the record being opened.
  if (opts.passDevice) Object.assign(headers, passHeaders());

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // A 401 only means "your session died" if this request was actually
  // relying on a stored token in the first place. skipAuth calls (login,
  // signup, superadmin login) never attach one — for those, a 401 means
  // "wrong credentials", and the server's real error message (surfaced
  // below via the normal !res.ok path) is what should reach the user,
  // not a forced logout of a session that never existed.
  if (res.status === 401 && !opts.skipAuth && !opts.public) {
    if (opts.superadminAuth) {
      setSuperadminToken(null);
      const err = new Error("Superadmin session expired. Please log in again.");
      err.isSuperadminAuthError = true;
      throw err;
    }
    // Same set of keys logout() clears — previously missing userId/orgType
    // here, so a session that ended via a silent 401 (rather than the
    // Sign Out button) left those two behind in sessionStorage.
    setToken(null);
    setUserId(null);
    setIsOrgAdmin(false);
    setOrgName(null);
    setOrgType(null);
    setOrgBrandColor(null);
    setOrgLogoDataUrl(null);
    const err = new Error("Session expired. Please log in again.");
    err.isAuthError = true;
    throw err;
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    const thrown = new Error(err.error || "Request failed");
    if (err.code) thrown.code = err.code;
    // Carried through so the log form can render the warning from the
    // server's answer rather than re-fetching it.
    if (err.activeProtocols) thrown.activeProtocols = err.activeProtocols;
    if (err.restrictedToken) thrown.restrictedToken = err.restrictedToken;
    if (err.organisationName) thrown.organisationName = err.organisationName;
    if (err.tier) thrown.tier = err.tier;
    // The phone app: a refused or restricted app sign-in still rotates the
    // device token, and the new one must reach the phone (app-devices.js).
    if (err.deviceToken) thrown.deviceToken = err.deviceToken;
    if (err.reason) thrown.reason = err.reason;
    thrown.status = res.status;
    throw thrown;
  }
  return res.json();
}

export const api = {
  // Public, unauthenticated — used by the parent tracking page (/track/:token)
  getPublicPlayerView: async (token) => {
    const res = await fetch(`${BASE}/api/public/players/${token}`, { headers: passHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "This link is invalid or has expired." }));
      throw new Error(err.error);
    }
    return res.json();
  },

  // Every player this same pass-holder may see at this organisation.
  // Returns null rather than throwing for the tokens that cannot answer
  // it — an older protocol-wide link, or one that has expired — because
  // the single-player page above it must still render either way.
  getPassPlayers: async (token) => {
    const res = await fetch(`${BASE}/api/public/pass/${token}/players`, { headers: passHeaders() });
    if (!res.ok) return null;
    return res.json();
  },

  acknowledgeGRTPUpdate: async (token) => {
    const res = await fetch(`${BASE}/api/public/players/${token}/acknowledge`, { method: "POST", headers: passHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Something went wrong." }));
      const thrown = new Error(err.error || "Something went wrong.");
      if (err.code) thrown.code = err.code;
      throw thrown;
    }
    return res.json();
  },

  login: async (email, password) => {
    let data;
    try {
      data = await req("POST", "/api/login", { email, password, ...signInClient() }, { skipAuth: true });
    } catch (e) {
      // trial_expired / pending_payment: no real session, but the server
      // hands back a restricted token so the "finish paying" screen's own
      // billing calls work on a genuinely fresh login, not just one that
      // still had a lingering token from before the org's status changed.
      if (e.restrictedToken) {
        setToken(e.restrictedToken);
        setOrgName(e.organisationName || "");
      }
      throw e;
    }
    // If MFA is required, the backend deliberately doesn't issue a real
    // token yet — just a pendingToken to exchange after the code is
    // verified. Don't store anything as a real session in that case.
    if (data.mfaRequired) return data;
    setToken(data.token);
    setUserId(data.userId || null);
    setIsOrgAdmin(data.isOrgAdmin);
    setOrgName(data.organisationName);
    setOrgType(data.orgType || "");
    setOrgBrandColor(data.brandColor || null);
    setOrgLogoDataUrl(data.logoDataUrl || null);
    return data;
  },
  verifyMfa: async (pendingToken, code) => {
    let data;
    try {
      data = await req("POST", "/api/login/verify-mfa", { pendingToken, code }, { skipAuth: true });
    } catch (e) {
      // Exactly as login does. An organisation whose trial has ended or
      // whose subscription is in its exit period gets a restricted session
      // AFTER the code is checked; without keeping it here, an admin with
      // two-factor turned on — which every admin must have — was shown
      // "trial_expired" as an error and could neither pay nor export.
      if (e.restrictedToken) {
        setToken(e.restrictedToken);
        setOrgName(e.organisationName || "");
      }
      throw e;
    }
    setToken(data.token);
    setUserId(data.userId || null);
    setIsOrgAdmin(data.isOrgAdmin);
    setOrgName(data.organisationName);
    setOrgType(data.orgType || "");
    setOrgBrandColor(data.brandColor || null);
    setOrgLogoDataUrl(data.logoDataUrl || null);
    return data;
  },
  // ── Phone app: staying signed in (backend/app-devices.js) ──
  // Swaps the device token unlocked by Face ID for a session, exactly as
  // login does. Errors carry the rotated deviceToken where there is one.
  appSession: async (deviceToken) => {
    let data;
    try {
      data = await req("POST", "/api/app/session", { deviceToken }, { skipAuth: true });
    } catch (e) {
      if (e.restrictedToken) {
        setToken(e.restrictedToken);
        setOrgName(e.organisationName || "");
      }
      throw e;
    }
    setToken(data.token);
    setUserId(data.userId || null);
    setIsOrgAdmin(data.isOrgAdmin);
    setOrgName(data.organisationName);
    setOrgType(data.orgType || "");
    setOrgBrandColor(data.brandColor || null);
    setOrgLogoDataUrl(data.logoDataUrl || null);
    return data;
  },
  registerAppDevice: (platform, label) => req("POST", "/api/app/devices", { platform, label }),
  getAppDevicesMine: () => req("GET", "/api/app/devices"),
  revokeOwnAppDevice: (deviceId) => req("DELETE", `/api/app/devices/${encodeURIComponent(deviceId)}`),
  setAppPushToken: (deviceId, token) => req("POST", `/api/app/devices/${encodeURIComponent(deviceId)}/push-token`, { token }),
  getOrgAppDevices: () => req("GET", "/api/organisation/app-devices"),
  revokeOrgAppDevice: (deviceId) => req("DELETE", `/api/organisation/app-devices/${encodeURIComponent(deviceId)}`),

  getMfaStatus: () => req("GET", "/api/mfa/status"),
  getActivityLog: () => req("GET", "/api/activity-log"),

  // ── Data protection ──
  // The two exports download a file rather than returning JSON to the
  // caller, so they go through fetch directly: these documents can be
  // large, and the point is to hand the admin a file they can send to a
  // parent or a DPO, not to render it on screen.
  getRetentionReport: () => req("GET", "/api/compliance/retention"),
  eraseDataSubject: (rosterPlayerId, confirmName) =>
    req("POST", `/api/compliance/subject/${rosterPlayerId}/erase`, { confirmName }),
  downloadComplianceFile: async (url, fallbackFilename) => {
    const token = getToken();
    const res = await fetch(`${BASE}${url}`, { headers: token ? { "x-auth-token": token } : {} });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || "Export failed.");
    }
    // Prefer the filename the server chose — it already carries the
    // person's name and the date, which is what makes the downloaded
    // file identifiable months later in a folder of similar exports.
    const disposition = res.headers.get("content-disposition") || "";
    const match = disposition.match(/filename="([^"]+)"/);
    return { blob: await res.blob(), filename: match ? match[1] : fallbackFilename };
  },
  exportOrganisationData: function () {
    return this.downloadComplianceFile("/api/compliance/export", "organisation-export.json");
  },
  // The full organisation export during the 60 days after a trial ends or
  // a subscription is cancelled, for the lapsed screen.
  downloadLapsedExport: function () {
    return this.downloadComplianceFile("/api/lapsed/export", "organisation-export.json");
  },
  exportSubjectAccess: function (rosterPlayerId) {
    return this.downloadComplianceFile(`/api/compliance/subject/${rosterPlayerId}`, "subject-access.json");
  },
  startMfaSetup: () => req("POST", "/api/mfa/setup"),
  confirmMfaSetup: (code) => req("POST", "/api/mfa/confirm", { code }),
  disableMfa: (password) => req("POST", "/api/mfa/disable", { password }),
  sendSupportRequest: (category, message, pageContext) =>
    req("POST", "/api/support", { category, message, pageContext }, { skipAuth: false }),
  signup: async (organisationName, orgType, tier, paymentRoute, email, password, tosAccepted, billing = {}) => {
    return req("POST", "/api/signup", { organisationName, orgType, tier, paymentRoute, email, password, tosAccepted: tosAccepted === true, ...billing }, { skipAuth: true });
  },
  getAnalytics: () => req("GET", "/api/analytics"),
  searchOrganisations: (q) => req("GET", `/api/organisations/search?q=${encodeURIComponent(q)}`, null, { public: true }),
  // The portal asks for the same emailed code as every other record link,
  // so each call carries the verified device.
  getParentPortal: (token) => req("GET", `/api/parent-portal/${token}`, null, { public: true, passDevice: true }),
  // The family confirming they have read the first concussion update.
  acknowledgeParentPortal: (token, playerId, rosterPlayerId) => req("POST", `/api/parent-portal/${token}/acknowledge`, { playerId, rosterPlayerId }, { public: true, passDevice: true }),
  submitParentChangeRequest: (token, changeType, payload, rosterPlayerId) => req("POST", `/api/parent-portal/${token}/request-change`, { changeType, payload, rosterPlayerId }, { public: true, passDevice: true }),
  sendParentPortalMessage: (token, body, images, rosterPlayerId) => req("POST", `/api/parent-portal/${token}/messages`, { body, images, rosterPlayerId }, { public: true, passDevice: true }),
  editParentPortalMessage: (token, messageId, body) => req("PUT", `/api/parent-portal/${token}/messages/${messageId}`, { body }, { public: true, passDevice: true }),
  // A photo needs the device header too, which an <img src> can't send:
  // fetched here and shown from a local object URL instead.
  getParentPortalAttachment: async (token, attachmentId) => {
    const res = await fetch(`${BASE}/api/parent-portal/${token}/attachments/${attachmentId}`, { headers: passHeaders() });
    if (!res.ok) throw new Error("This photo couldn't be loaded.");
    return URL.createObjectURL(await res.blob());
  },
  getParentChangeRequests: () => req("GET", "/api/parent-change-requests"),
  approveParentChange: (id) => req("POST", `/api/parent-change-requests/${id}/approve`),
  rejectParentChange: (id, reason) => req("POST", `/api/parent-change-requests/${id}/reject`, { reason }),
  getSuperadminAnalytics: () => req("GET", "/api/superadmin/analytics", null, { superadminAuth: true }),
  getBackupStatus: () => req("GET", "/api/superadmin/backup-status", null, { superadminAuth: true }),
  getSuperadminEmailHealth: () => req("GET", "/api/superadmin/email-health", null, { superadminAuth: true }),
  runBackupNow: () => req("POST", "/api/superadmin/backup/run", {}, { superadminAuth: true }),
  subscribeMailingList: (email, name, source) => req("POST", "/api/mailing-list/subscribe", { email, name, source }, { public: true }),
  unsubscribeMailingList: (token) => req("GET", `/api/mailing-list/unsubscribe/${token}`, null, { public: true }),
  getSuperadminMailingList: () => req("GET", "/api/superadmin/mailing-list", null, { superadminAuth: true }),
  sendMailingListCampaign: (subject, body) => req("POST", "/api/superadmin/mailing-list/send", { subject, body }, { superadminAuth: true }),
  getTrialBillingDetails: () => req("GET", "/api/trial/billing"),
  requestTrialInvoice: (body) => req("POST", "/api/trial/request-invoice", body),
  createCheckoutSession: (planKey) => req("POST", "/api/billing/create-checkout-session", { planKey }),
  createBillingPortalSession: () => req("POST", "/api/billing/portal-session"),
  verifyEmail: async (token) => {
    return req("POST", `/api/verify-email/${token}`, null, { skipAuth: true });
  },
  logout: async () => {
    try { await req("POST", "/api/logout"); } catch {}
    setToken(null);
    setUserId(null);
    setIsOrgAdmin(false);
    setOrgName(null);
    setOrgType(null);
    setOrgBrandColor(null);
    setOrgLogoDataUrl(null);
  },
  isLoggedIn: () => !!getToken(),
  userId: () => getUserId(),
  isOrgAdmin: () => getIsOrgAdmin(),
  orgName: () => getOrgName(),
  orgType: () => getOrgType(),
  orgBrandColor: () => getOrgBrandColor(),
  orgLogoDataUrl: () => getOrgLogoDataUrl(),
  updateOrganisationBranding: async (brandColor, logoDataUrl) => {
    const body = {};
    if (brandColor !== undefined) body.brandColor = brandColor;
    if (logoDataUrl !== undefined) body.logoDataUrl = logoDataUrl;
    const data = await req("POST", "/api/organisation/branding", body);
    setOrgBrandColor(data.brandColor);
    setOrgLogoDataUrl(data.logoDataUrl);
    return data;
  },
  getOrganisationStatus: () => req("GET", "/api/organisation/status"),
  getPlayers: (includeArchived = false) => req("GET", `/api/players${includeArchived ? "?includeArchived=true" : ""}`),
  createPlayer: (data) => req("POST", "/api/players", data),
  symptomReturn: (id) => req("POST", `/api/players/${id}/symptom-return`),
  removeEarly: (id, reason) => req("POST", `/api/players/${id}/remove-early`, { reason }),
  undoSymptomReturn: (id) => req("POST", `/api/players/${id}/undo-symptom-return`),
  deletePlayer: (id) => req("DELETE", `/api/players/${id}`),
  unarchivePlayer: (id) => req("POST", `/api/players/${id}/unarchive`),
  downloadPDF: async (id) => {
    const token = getToken();
    const res = await fetch(`${BASE}/api/players/${id}/pdf`, {
      headers: token ? { "x-auth-token": token } : {},
    });
    if (!res.ok) throw new Error("PDF generation failed");
    return res.blob();
  },
  exportData: async () => {
    // Raw fetch rather than req() — we want the actual file blob, not JSON parsed for app use
    const token = getToken();
    const res = await fetch(`${BASE}/api/export`, {
      headers: token ? { "x-auth-token": token } : {},
    });
    if (!res.ok) throw new Error("Export failed");
    return res.blob();
  },
  // `selection` is the list of record ids on screen, sent so the file
  // matches what the person exporting it just read. Omit it entirely to
  // export everything — passing an empty array exports nothing, which is
  // the correct answer when a filter matched nothing.
  exportCSV: async (selection) => {
    const token = getToken();
    const res = await fetch(`${BASE}/api/export/csv`, {
      method: selection ? "POST" : "GET",
      headers: {
        ...(token ? { "x-auth-token": token } : {}),
        ...(selection ? { "Content-Type": "application/json" } : {}),
      },
      ...(selection ? { body: JSON.stringify({ ids: selection }) } : {}),
    });
    if (!res.ok) throw new Error("CSV export failed");
    return res.blob();
  },
  exportAllPDF: async (selection, filterDescription) => {
    const token = getToken();
    const res = await fetch(`${BASE}/api/export/pdf`, {
      method: selection ? "POST" : "GET",
      headers: {
        ...(token ? { "x-auth-token": token } : {}),
        ...(selection ? { "Content-Type": "application/json" } : {}),
      },
      ...(selection ? { body: JSON.stringify({ ids: selection, filterDescription }) } : {}),
    });
    if (!res.ok) throw new Error("PDF export failed");
    return res.blob();
  },

  // ── Injuries and first aid ───────────────────────────────────────────
  // A register, not a status tracker. Nothing here advances anything and
  // nothing reports anything to anybody outside the club.
  createInjury: (data) => req("POST", "/api/injuries", data),
  getInjuries: (filters = {}) => {
    const q = new URLSearchParams(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== "")
    ).toString();
    return req("GET", `/api/injuries${q ? `?${q}` : ""}`);
  },
  getInjury: (id) => req("GET", `/api/injuries/${id}`),
  updateInjury: (id, data) => req("PUT", `/api/injuries/${id}`, data),
  shareInjury: (id, data) => req("POST", `/api/injuries/${id}/share`, data),
  answerInjuryFollowup: (id, admitted) => req("POST", `/api/injuries/${id}/followup`, { admitted }),
  getCrossOrgInjuries: () => req("GET", "/api/cross-org-injuries"),
  // Public: the page behind the link every recipient is sent. No session,
  // because the person holding it has no account here.
  getInjuryRecord: (token) => req("GET", `/api/injury-record/${token}`, null, { public: true, passDevice: true }),
  acknowledgeInjuryRecord: (token) => req("POST", `/api/injury-record/${token}/acknowledge`, null, { public: true, passDevice: true }),

  // ── Proving who is holding a link ────────────────────────────────────
  // The parent portal has its own pair of routes (its token is not a
  // pass), but the codes and the remembered device are the same.
  requestPassCode: async (token, { portal = false } = {}) => {
    const res = await fetch(`${BASE}${portal ? `/api/parent-portal/${token}` : `/api/public/pass/${token}`}/verify/request`, {
      method: "POST", headers: { "Content-Type": "application/json" },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || "Could not send a code.");
    return body;
  },
  verifyPassCode: async (token, code, { portal = false } = {}) => {
    const res = await fetch(`${BASE}${portal ? `/api/parent-portal/${token}` : `/api/public/pass/${token}`}/verify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || "That code did not work.");
    rememberPassDevice(body.deviceToken);
    return body;
  },

  // The accident book as one file. The ids on screen are always sent —
  // both lists, always, even when nothing is filtered — so the number on
  // the button and the number of rows in the file cannot drift apart.
  exportFirstAidLog: async ({ format, injuryIds, concussionIds, filterDescription }) => {
    const token = getToken();
    const res = await fetch(`${BASE}/api/first-aid-log/export`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { "x-auth-token": token } : {}) },
      body: JSON.stringify({ format, injuryIds, concussionIds, filterDescription }),
    });
    if (!res.ok) throw new Error("First aid log export failed");
    return res.blob();
  },

  // ── Staff management (org admin only) ─────────────────────────────
  getStaff: () => req("GET", "/api/staff"),
  inviteStaff: (email) => req("POST", "/api/staff/invite", { email }),
  removeStaff: (id) => req("DELETE", `/api/staff/${id}`),
  toggleStaffAdmin: (id) => req("POST", `/api/staff/${id}/toggle-admin`),
  setStaffAgeGrades: (id, ageGrades) => req("PUT", `/api/staff/${id}/age-grades`, { ageGrades }),
  setStaffTeams: (id, teams) => req("PUT", `/api/staff/${id}/teams`, { teams }),

  // ── Teams (independent of age grade — e.g. 1st XV, U12 Girls) ────────
  getTeams: () => req("GET", "/api/teams"),
  addTeam: (name) => req("POST", "/api/teams", { name }),
  deleteTeam: (id) => req("DELETE", `/api/teams/${id}`),

  // ── Parent/staff messaging ───────────────────────────────────────────
  getMessageThreads: () => req("GET", "/api/messages"),
  getMessagesUnreadCount: () => req("GET", "/api/messages/unread-count"),
  getMessageThread: (threadId) => req("GET", `/api/messages/${threadId}`),
  sendThreadMessage: (threadId, body, images) => req("POST", `/api/messages/${threadId}`, { body, images }),
  editStaffMessage: (threadId, messageId, body) => req("PUT", `/api/messages/${threadId}/${messageId}`, { body }),
  // Attachments need the same x-auth-token header as every other staff
  // request, which a plain <img src> can't send — so this fetches the
  // bytes directly and hands back an object URL for an <img> to use.
  fetchMessageAttachmentUrl: async (attachmentId) => {
    const token = getToken();
    const res = await fetch(`${BASE}/api/messages/attachments/${attachmentId}`, {
      headers: token ? { "x-auth-token": token } : {},
    });
    if (!res.ok) throw new Error("Couldn't load this photo.");
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  // ── Roster ─────────────────────────────────────────────────────────
  getRoster: () => req("GET", "/api/roster"),
  getArchivedRoster: () => req("GET", "/api/roster/archived"),

  // Parent registration
  getRegistrationOrgInfo: async (token) => {
    const res = await fetch(`${BASE}/api/register/${token}`);
    if (!res.ok) { const e = await res.json().catch(() => ({ error: "Invalid link." })); throw new Error(e.error); }
    return res.json();
  },
  submitParentRegistration: async (token, data) => {
    const res = await fetch(`${BASE}/api/register/${token}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({ error: "Submission failed." })); throw new Error(e.error); }
    return res.json();
  },
  getPendingRegistrations: () => req("GET", "/api/registrations/pending"),
  getPendingRegistrationsCount: () => req("GET", "/api/registrations/count"),
  approveRegistration: (id, force) => req("POST", `/api/registrations/${id}/approve`, force ? { force: true } : {}),
  bulkApproveRegistrations: (ids) => req("POST", "/api/registrations/bulk-approve", { ids }),
  rejectRegistration: (id, notes, notifyParent, parentReason) => req("POST", `/api/registrations/${id}/reject`, { notes, notifyParent, parentReason }),
  getRegistrationLink: () => req("GET", "/api/registrations/link"),
  getAdultRegistration: (token) => req("GET", `/api/register/adult/${token}`, null, { skipAuth: true }),
  getStaffRegistration: (token) => req("GET", `/api/staff-registration/${token}`, null, { skipAuth: true }),
  submitStaffRegistration: (token, data) => req("POST", `/api/staff-registration/${token}`, data, { skipAuth: true }),
  getPendingStaffRegistrations: () => req("GET", "/api/staff-registrations/pending"),
  approveStaffRegistration: (id, ageGrades, teams) => req("POST", `/api/staff-registrations/${id}/approve`, { ageGrades, teams }),
  rejectStaffRegistration: (id) => req("POST", `/api/staff-registrations/${id}/reject`),
  searchOrgDirectory: (type, search) => req("GET", `/api/orgs/directory?type=${encodeURIComponent(type)}&search=${encodeURIComponent(search)}`, null, { skipAuth: true }),
  getCrossOrgAlerts: () => req("GET", "/api/cross-org-alerts"),
  getEmailHealth: () => req("GET", "/api/email-health"),
  resolveEmailBounce: (playerId, recipientEmail) => req("POST", `/api/players/${playerId}/email-bounces/resolve`, { recipientEmail }),
  getCrossOrgProtocols: () => req("GET", "/api/cross-org-protocols"),
  getPlayerLinkRequests: () => req("GET", "/api/player-link-requests"),
  resolvePlayerLinkRequest: (requestId, rosterPlayerId) =>
    req("POST", `/api/player-link-requests/${requestId}/resolve`, { rosterPlayerId }),
  rejectPlayerLinkRequest: (requestId) =>
    req("POST", `/api/player-link-requests/${requestId}/reject`, {}),
  addPlayerFromNomination: (requestId, force) =>
    req("POST", `/api/player-link-requests/${requestId}/add-to-roster`, { force: !!force }),
  getContactInvite: (token) => req("GET", `/api/contact-invite/${token}`, null, { public: true }),
  confirmContactInvite: (token) => req("POST", `/api/contact-invite/${token}/confirm`, {}, { public: true }),
  removeContactInvite: (token) => req("POST", `/api/contact-invite/${token}/remove`, {}, { public: true }),
  getLinkedStatus: (rosterPlayerId) => req("GET", `/api/roster/${rosterPlayerId}/linked-status`),
  dismissCrossOrgAlert: (id) => req("DELETE", `/api/cross-org-alerts/${id}`),
  submitAdultRegistration: (token, body) => req("POST", `/api/register/adult/${token}`, body, { skipAuth: true }),
  rosterBulkReplacePreview: (field, oldValue) => req("POST", "/api/roster/bulk-replace", { field, oldValue, dry: true }),
  rosterBulkReplaceConfirm: (field, oldValue, newValue) => req("POST", "/api/roster/bulk-replace", { field, oldValue, newValue, dry: false }),
  // Returns the file; the caller saves it (nativeApp.saveFile), which on
  // the phone app means the share sheet rather than a download link.
  downloadSarExport: async (id) => {
    const res = await fetch(`${BASE}/api/players/${id}/sar-export`, {
      headers: { "x-auth-token": getToken() },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "SAR export failed" }));
      throw new Error(err.error);
    }
    return res.blob();
  },
  getPlayerNotifications: (id) => req("GET", `/api/players/${id}/notifications`),
  getAgeGradeAdvancementStatus: () => req("GET", "/api/age-grade-advancement/status"),
  decideAgeGradeAdvancement: (action) => req("POST", "/api/age-grade-advancement/decide", { action }),
  searchRoster: (query) => req("GET", `/api/roster/search?q=${encodeURIComponent(query)}`),
  addRosterPlayer: (data) => req("POST", "/api/roster", data),
  updateRosterPlayer: (id, data) => req("PUT", `/api/roster/${id}`, data),
  deleteRosterPlayer: (id) => req("DELETE", `/api/roster/${id}`),
  deleteRosterByAgeGrade: (ageGrade) => req("DELETE", `/api/roster/by-age-grade/${ageGrade}`),
  deleteEntireRoster: () => req("DELETE", "/api/roster/all"),
  previewRosterImport: (csvText) => req("POST", "/api/roster/import/preview", { csvText }),
  confirmRosterImport: (csvText) => req("POST", "/api/roster/import/confirm", { csvText }),

  // ── Accept invite (public, no auth) ───────────────────────────────
  getInviteDetails: async (token) => {
    const res = await fetch(`${BASE}/api/accept-invite/${token}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "This invite link is invalid or has expired." }));
      throw new Error(err.error);
    }
    return res.json();
  },
  acceptInvite: async (token, password) => {
    return req("POST", `/api/accept-invite/${token}`, { password }, { skipAuth: true });
  },
  forgotPassword: async (email) => {
    return req("POST", "/api/forgot-password", { email }, { skipAuth: true });
  },
  getResetPasswordValidity: async (token) => {
    const res = await fetch(`${BASE}/api/reset-password/${token}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "This reset link is invalid or has expired." }));
      throw new Error(err.error);
    }
    return res.json();
  },
  resetPassword: async (token, password) => {
    return req("POST", `/api/reset-password/${token}`, { password }, { skipAuth: true });
  },

  // ── Destructive actions (gated server-side by org-admin status) ───
  // No separate password or session anymore — these use the person's
  // normal login. The server checks they're actually an org admin
  // (requireOrgAdmin) before allowing either of these through.
  permanentlyDeletePlayer: (id) => req("DELETE", `/api/admin/players/${id}`),
  clearAllData: () => req("POST", "/api/admin/clear-all"),

  // ── Superadmin (you, across every organisation) ───────────────────
  superadminLogin: async (password, code) => {
    const data = await req("POST", "/api/superadmin/login", { password, code }, { skipAuth: true });
    setSuperadminToken(data.token);
    return data;
  },
  superadminLogout: async () => {
    try { await req("POST", "/api/superadmin/logout", null, { superadminAuth: true }); } catch {}
    setSuperadminToken(null);
  },
  isSuperadminLoggedIn: () => !!getSuperadminToken(),
  getAllOrganisations: () => req("GET", "/api/superadmin/organisations", null, { superadminAuth: true }),
  getOrganisationDetail: (id) => req("GET", `/api/superadmin/organisations/${id}`, null, { superadminAuth: true }),
  setOrganisationStatus: (id, status) => req("POST", `/api/superadmin/organisations/${id}/status`, { status }, { superadminAuth: true }),
  recordInvoicePayment: (id, paidUntil) => req("POST", `/api/superadmin/organisations/${id}/record-invoice-payment`, { paidUntil }, { superadminAuth: true }),
  setOrganisationTestFlag: (id, isTest) => req("POST", `/api/superadmin/organisations/${id}/test-flag`, { isTest }, { superadminAuth: true }),
  setRegistrationApproval: (id, approved) => req("POST", `/api/superadmin/organisations/${id}/registration-approval`, { approved }, { superadminAuth: true }),
  setOrganisationOrgType: (id, orgType) => req("POST", `/api/superadmin/organisations/${id}/org-type`, { orgType }, { superadminAuth: true }),
  deleteOrganisation: (id, confirmName) => req("DELETE", `/api/superadmin/organisations/${id}`, { confirmName }, { superadminAuth: true }),
  setOrganisationMaxUsers: (id, maxUsers) => req("POST", `/api/superadmin/organisations/${id}/max-users`, { maxUsers }, { superadminAuth: true }),
  // Account recovery. Neither sets nor returns a password: one clears the
  // second factor, the other sends the user the same reset link the login
  // page produces, so the new password is known only to them.
  resetUserMfa: (userId) => req("POST", `/api/superadmin/users/${userId}/reset-mfa`, {}, { superadminAuth: true }),
  sendUserPasswordReset: (userId) => req("POST", `/api/superadmin/users/${userId}/send-password-reset`, {}, { superadminAuth: true }),
  getSuperadminAuditLog: () => req("GET", "/api/superadmin/audit-log", null, { superadminAuth: true }),
  getAllOrgsActivityLog: () => req("GET", "/api/superadmin/activity-log", null, { superadminAuth: true }),
  setOrganisationMaxPlayers: (id, maxPlayers) => req("POST", `/api/superadmin/organisations/${id}/max-players`, { maxPlayers }, { superadminAuth: true }),
};
